import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPatch, apiPost } from 'api'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'

const getRoles = async (token, municipalityId) => {
  const url = `municipalities/${municipalityId}/roles?as_options=true`
  const data = await apiGet({ url, token })
  return data
}

export default function useRoles({ options }) {
  const token = useContext(AuthContext)
  const { municipality_id } = useContext(UserContext)
  const queryFn = () => getRoles(token, municipality_id)
  const queryKey = 'roles?as_options=true'
  return useQuery(queryKey, queryFn, { ...options })
}

const getRole = async (token, params) => {
  const url = `roles/${params.id}`

  const data = await apiGet({ url, token, params })
  return data
}

export function useRole({ params }) {
  const token = useContext(AuthContext)

  return useQuery(['role', params], () => getRole(token, params))
}

export function useUpdateRole() {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    ({ roleId, ...params }) =>
      apiPatch({
        url: `roles/${roleId}`,
        token,
        objectName: 'role',
        params
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('roles')
        queryClient.invalidateQueries('role')
      }
    }
  )
}

export function useGenerateRoleDescription() {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()

  return useMutation(
    ({ roleId }) =>
      apiPost({
        url: `roles/${roleId}/ai_description`,
        token,
        objectName: 'role',
        params: {}
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('role')
      }
    }
  )
}

export function useSearchRoles({ search, enabled = false }) {
  const token = useContext(AuthContext)
  const { municipality_id } = useContext(UserContext)

  return useQuery(
    ['roles', 'search', municipality_id, search],
    () =>
      apiGet({
        url: `municipalities/${municipality_id}/roles`,
        token,
        params: { search, perPage: 1000, page: 1 }
      }),
    { enabled, refetchOnWindowFocus: false }
  )
}

export function useBulkUpdateRoleType() {
  const token = useContext(AuthContext)
  const { municipality_id } = useContext(UserContext)
  const queryClient = useQueryClient()

  return useMutation(
    ({ roleIds, roleType }) =>
      apiPatch({
        url: `municipalities/${municipality_id}/roles/bulk_update_role_type`,
        token,
        objectName: 'bulk_update',
        params: { role_ids: roleIds, role_type: roleType }
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('roles')
      }
    }
  )
}
